import * as React from "react";

import { api } from "@repo/backend";
import { Button } from "@repo/ui/components/button";
import { Field, FieldError, FieldGroup, FieldLabel } from "@repo/ui/components/field";
import { Input } from "@repo/ui/components/input";
import { Skeleton } from "@repo/ui/components/skeleton";
import { useForm } from "@tanstack/react-form";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery } from "convex/react";
import { ConvexError } from "convex/values";
import { format, parseISO } from "date-fns";
import {
  CalendarCheckIcon,
  Loader2Icon,
  LockIcon,
  MailCheckIcon,
  ShieldCheckIcon,
} from "lucide-react";
import { toast } from "sonner";
import { z } from "zod";

import {
  type ScheduleInfo,
  type Slot,
  timezoneLabel,
} from "@/components/scheduling/scheduling-layout";
import { sessionTypeSchema } from "@/lib/scheduling";

export const Route = createFileRoute("/scheduling/confirm")({
  head: () => ({
    meta: [
      { title: "Confirm Your Booking | WorkGrid" },
      {
        name: "description",
        content:
          "Confirm your staff photo session booking. Your Voxis links to the final assets will be delivered to the email address you provide.",
      },
    ],
  }),
  validateSearch: z.object({
    type: sessionTypeSchema,
    start: z.coerce.number().int().positive(),
  }),
  component: ConfirmBookingPage,
});

const guestSchema = z.object({
  name: z.string().trim().min(1, "Enter your name.").max(120),
  email: z
    .string()
    .trim()
    .email("Enter a valid email address.")
    .refine((email) => !email.toLowerCase().endsWith("efg.gg"), {
      message: "EFG emails are not accepted. Please use a private email address.",
    }),
});

const fullDate = (date: string) => format(parseISO(`${date}T12:00:00`), "EEEE, MMMM d, yyyy");

function ConfirmBookingPage() {
  const { type, start } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const schedule = useQuery(api.scheduling.getPublicSchedule, { type });
  const createBooking = useMutation(api.scheduling.createBooking);
  const slot = schedule?.slots.find((candidate) => candidate.startAt === start);
  const form = useForm({
    defaultValues: { name: "", email: "" },
    validators: { onChange: guestSchema },
    onSubmit: async ({ value }) => {
      try {
        const { confirmationToken } = await createBooking({ type, startAt: start, ...value });
        await navigate({
          to: "/scheduling/confirmed/$token",
          params: { token: confirmationToken },
        });
      } catch (error) {
        toast.error(
          error instanceof ConvexError ? String(error.data) : "Could not complete the booking.",
        );
      }
    },
  });

  if (schedule === undefined) return <ConfirmSkeleton />;

  if (!slot) {
    return (
      <section className="flex flex-col items-center justify-center bg-[#191919] p-8 text-center">
        <h2 className="text-lg font-semibold text-white">This time is no longer available</h2>
        <p className="mt-2 text-sm text-white/55">
          Return to the calendar and choose another time.
        </p>
        <Button
          className="mt-5"
          onClick={() => navigate({ to: "/scheduling/calendar", search: { type } })}
        >
          Choose another time
        </Button>
      </section>
    );
  }

  return (
    <section className="min-h-0 overflow-auto bg-[#191919] p-5 text-white sm:p-8">
      <h2 className="px-12 text-center text-lg font-semibold italic">Confirm booking</h2>
      <form
        className="mx-auto mt-6 grid w-full max-w-xl gap-6"
        onSubmit={(event) => {
          event.preventDefault();
          event.stopPropagation();
          void form.handleSubmit();
        }}
      >
        <section className="rounded-lg border border-white/10 bg-white/[0.03] p-4">
          <h3 className="text-sm font-semibold">Booking details</h3>
          <SlotSummary config={schedule.config as ScheduleInfo} slot={slot as Slot} />
        </section>
        <FieldGroup>
          <form.Field name="name">
            {(field) => {
              const invalid = field.state.meta.isTouched && !field.state.meta.isValid;
              return (
                <Field data-invalid={invalid}>
                  <FieldLabel htmlFor="booking-name">Name *</FieldLabel>
                  <Input
                    id="booking-name"
                    className="border-white/15 bg-white/[0.04] text-white"
                    autoComplete="name"
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(event) => field.handleChange(event.target.value)}
                  />
                  {invalid ? <FieldError errors={field.state.meta.errors} /> : null}
                </Field>
              );
            }}
          </form.Field>
          <form.Field name="email">
            {(field) => {
              const invalid = field.state.meta.isTouched && !field.state.meta.isValid;
              return (
                <Field data-invalid={invalid}>
                  <FieldLabel htmlFor="booking-email">Email *</FieldLabel>
                  <Input
                    id="booking-email"
                    className="border-white/15 bg-white/[0.04] text-white"
                    type="email"
                    autoComplete="email"
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(event) => field.handleChange(event.target.value)}
                  />
                  <div className="mt-1.5 space-y-1 text-xs text-white/55">
                    <p className="flex items-center gap-1.5">
                      <ShieldCheckIcon className="size-3.5 shrink-0" />
                      Your email is used only for your booking and photo delivery — never shared
                    </p>
                    <p className="flex items-center gap-1.5">
                      <MailCheckIcon className="size-3.5 shrink-0" />
                      You will receive your photos in an email to this address
                    </p>
                    <p className="flex items-center gap-1.5">
                      <LockIcon className="size-3.5 shrink-0" />
                      One link contains only your photos — your private gallery, not published
                      anywhere
                    </p>
                  </div>
                  {invalid ? <FieldError errors={field.state.meta.errors} /> : null}
                </Field>
              );
            }}
          </form.Field>
          <form.Subscribe selector={(state) => [state.canSubmit, state.isSubmitting] as const}>
            {([canSubmit, isSubmitting]) => (
              <Button className="mt-2 w-full" type="submit" disabled={!canSubmit || isSubmitting}>
                {isSubmitting ? <Loader2Icon className="animate-spin" /> : <CalendarCheckIcon />}
                Book now
              </Button>
            )}
          </form.Subscribe>
        </FieldGroup>
      </form>
    </section>
  );
}

function ConfirmSkeleton() {
  return (
    <section className="min-h-0 bg-[#191919] p-5 sm:p-8">
      <h2 className="px-12 text-center text-lg font-semibold text-white italic">Confirm booking</h2>
      <div className="mx-auto mt-6 grid w-full max-w-xl gap-6">
        <div className="rounded-lg border border-white/10 p-4">
          <p className="text-sm font-semibold text-white">Booking details</p>
          <div className="mt-4 grid grid-cols-[5rem_1fr] gap-x-4 gap-y-3">
            {Array.from({ length: 5 }, (_, index) => (
              <React.Fragment key={index}>
                <Skeleton className="h-4 w-14 bg-white/10" />
                <Skeleton className="h-4 w-full bg-white/10" />
              </React.Fragment>
            ))}
          </div>
        </div>
        <div className="space-y-6">
          <div>
            <p className="text-sm font-medium text-white">Name *</p>
            <Skeleton className="mt-2 h-9 w-full bg-white/10" />
          </div>
          <div>
            <p className="text-sm font-medium text-white">Email *</p>
            <p className="mt-1 text-sm text-white/55">
              This email will be used to deliver your Voxis links to the final assets.
            </p>
            <Skeleton className="mt-2 h-9 w-full bg-white/10" />
          </div>
          <Skeleton className="h-9 w-full bg-white/10" />
        </div>
      </div>
    </section>
  );
}

function SlotSummary({ config, slot }: { config: ScheduleInfo; slot: Slot }) {
  return (
    <dl className="mt-4 grid grid-cols-[5rem_1fr] gap-x-4 gap-y-3 text-sm">
      <dt className="text-white/50">Session</dt>
      <dd className="font-medium">{config.sessionLabel}</dd>
      <dt className="text-white/50">Date</dt>
      <dd>{fullDate(slot.localDate)}</dd>
      <dt className="text-white/50">Time</dt>
      <dd>
        {slot.localStartTime}–{slot.localEndTime}
      </dd>
      <dt className="text-white/50">Timezone</dt>
      <dd>{timezoneLabel(config.timezone)}</dd>
      <dt className="text-white/50">Duration</dt>
      <dd>{config.slotDurationMinutes} min</dd>
    </dl>
  );
}
